import { Plural, Trans } from "@lingui/macro";
import {
  IUiDialogResponsiveProps,
  UiBox,
  UiCard,
  UiCardActions,
  UiCardContent,
  UiDialogResponsive,
  UiGrid,
  UiHelper,
  UiLink,
  UiTypography,
} from "@smp/ui";
import { dataArrayUnique, textCapitalize } from "@smp/utils";
import differenceInDays from "date-fns/differenceInDays";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";

import { HelperAdapterLink } from "@/components/helpers/helper_adapter_link";
import { useDateCurrent } from "@/hooks/use_date_current";
import { useEpPageconfigListRecursive } from "@/hooks/use_ep_pageconfig";
import { useHumanizer } from "@/hooks/use_humanizer";
import { useContextApp } from "@/screens/app/context/app";

import { Pages } from "../../_shared";

export const DialogRecurring = (props: IUiDialogResponsiveProps) => {
  const {
    state: { planLast, plansubscriptionLast, recurringPaymentActive },
  } = useContextApp();

  const { dateCurrent } = useDateCurrent();

  const { pageconfigList } = useEpPageconfigListRecursive({
    req: {
      query: { all_organization_contexts: "True", has_scheduled_posts: true },
    },
  });

  const { humanizeBillingPlanId } = useHumanizer();

  const chargeNextAt = recurringPaymentActive?.charge_next_at;

  const pagesUsed = pageconfigList
    ? dataArrayUnique(
        pageconfigList.map(
          (pageconfig) => `${pageconfig.medium}:${pageconfig.external_id}`,
        ),
      ).length
    : undefined;

  const pagesLimit = planLast ? planLast.social_pages_limit : 0;

  const plansubscriptionLastDaysLeft = plansubscriptionLast
    ? (() => {
        const diff = differenceInDays(
          parseISO(plansubscriptionLast.period_end),
          dateCurrent,
        );
        return diff > 0 ? diff : 0;
      })()
    : undefined;

  const handleClose = () => {
    const { onClose } = props;
    onClose && onClose({}, "backdropClick");
  };

  return (
    <UiDialogResponsive
      fullWidth
      maxWidth="sm"
      title={<Trans>Plan subscription</Trans>}
      {...props}
    >
      <UiGrid container spacing={2}>
        <UiGrid item xs={12} sm={4}>
          <UiCard color="primary" fullHeight fullWidth>
            <UiCardContent>
              <UiTypography gutterBottom>
                {planLast ? (
                  humanizeBillingPlanId(planLast.id) ||
                  textCapitalize(planLast.name)
                ) : (
                  <Trans>Not selected</Trans>
                )}
              </UiTypography>
            </UiCardContent>
            <UiCardActions>
              <UiLink
                color="inherit"
                component={HelperAdapterLink}
                onClick={handleClose}
                to="/billing/payments/subscription_settings"
                underline="always"
              >
                <Trans>Subscription settings</Trans>
              </UiLink>
            </UiCardActions>
          </UiCard>
        </UiGrid>
        {recurringPaymentActive && chargeNextAt && (
          <UiGrid item xs={12} sm={4}>
            <UiCard color="info" fullHeight fullWidth>
              <UiCardContent>
                <UiTypography>
                  <Trans>Next charge</Trans>
                </UiTypography>
                <UiTypography>
                  {format(parseISO(chargeNextAt), "dd.MM.yyyy")}
                </UiTypography>
              </UiCardContent>
            </UiCard>
          </UiGrid>
        )}
        {!recurringPaymentActive && plansubscriptionLastDaysLeft !== undefined && (
          <UiGrid item xs={12} sm={4}>
            <UiCard
              color={
                plansubscriptionLastDaysLeft <= 0
                  ? "error"
                  : plansubscriptionLastDaysLeft <= 3
                  ? "warning"
                  : "info"
              }
              fullHeight
              fullWidth
            >
              <UiCardContent>
                <UiTypography>
                  {plansubscriptionLastDaysLeft <= 0 ? (
                    <Trans>Subscription expired</Trans>
                  ) : (
                    <Trans>Subscription expires</Trans>
                  )}
                </UiTypography>
                <UiTypography>
                  (
                  <Plural
                    zero={`# days`}
                    one={`# day`}
                    few={`# days`}
                    many={`# days`}
                    other={`# days`}
                    value={plansubscriptionLastDaysLeft}
                  />
                  )
                </UiTypography>
              </UiCardContent>
              <UiCardActions>
                <UiLink
                  color="inherit"
                  component={HelperAdapterLink}
                  onClick={handleClose}
                  to="/billing/payments/subscription_settings"
                  underline="always"
                >
                  <Trans>__dialog_recurring__Renew</Trans>
                </UiLink>
              </UiCardActions>
            </UiCard>
          </UiGrid>
        )}
        {planLast && (
          <UiGrid item xs={12} sm={4}>
            <UiBox display="flex" flexDirection="column" height="100%">
              <UiBox flexGrow={1} />
              <UiBox
                alignItems="center"
                display="flex"
                flexDirection="row"
                height={48}
                paddingRight={1}
              >
                <UiBox
                  alignItems="center"
                  display="flex"
                  flexDirection="row"
                  flexGrow={1}
                >
                  <UiTypography>
                    <Trans>In use</Trans>
                  </UiTypography>
                  <UiHelper
                    title={
                      <UiTypography>
                        <Trans>
                          The number of social pages that support simultaneous
                          planned publications. This set of pages can be changed
                          at any time throughout the subscription period. Our
                          system automatically lists only active pages with
                          scheduled publications.
                        </Trans>
                      </UiTypography>
                    }
                  />
                </UiBox>
                <UiBox flexGrow={1}>
                  <UiTypography align="right">{pagesUsed}</UiTypography>
                </UiBox>
              </UiBox>
              <UiBox
                alignItems="center"
                borderTop="solid 1px #EEE"
                display="flex"
                flexDirection="row"
                height={48}
                paddingRight={1}
              >
                <UiBox flexGrow={1}>
                  <UiTypography>
                    <Trans>Total</Trans>
                  </UiTypography>
                </UiBox>
                <UiBox flexGrow={1}>
                  <UiTypography align="right">{pagesLimit ?? "-"}</UiTypography>
                </UiBox>
              </UiBox>
              <UiBox flexGrow={1} />
            </UiBox>
          </UiGrid>
        )}
        <Pages onClose={props.onClose} />
      </UiGrid>
    </UiDialogResponsive>
  );
};
